import * as React from "react";
import { cn } from "../lib/cn";
import { nxSvgPath, type NxSize } from "../lib/onyx-material";
import { useMeasure } from "../hooks/useMeasure";

/**
 * NxKeyline — the CONTINUOUS accent/hairline keyline for any `nx-*` notched
 * surface, drawn as an SVG stroke OVERLAY.
 *
 * Why an overlay and not a border / inset box-shadow: a `clip-path` slices ALL
 * of an element's paint, so a `border` (and an inset `box-shadow`) is cut at the
 * notch chamfer and feathered at the rounded corners — the line drops out
 * exactly where the notch is (issue #158). This primitive renders an
 * absolutely-positioned, `pointer-events:none` `<svg>` SIBLING that sits OVER
 * the clipped panel. The SVG is NOT itself clip-path-clipped, so its single
 * `<path>` — which traces the EXACT notched silhouette (lines + arcs) via
 * {@link nxSvgPath} — is fully continuous around every side, every rounded
 * corner, AND across the notch diagonal, in both rest and active states.
 *
 * Usage: drop it as the LAST child of a `position: relative` element that
 * carries the matching `nx-*` notch clip. It measures its parent and sizes the
 * path in real px, so the arcs never skew on a non-square box:
 *
 *   <div className={cn(nx.l, "relative ...")}>
 *     ...content...
 *     <NxKeyline nx="l" active={selected} />
 *   </div>
 *
 * The stroke is CENTERED on the path, so the path is inset by `strokeWidth / 2`
 * to keep the whole line inside the box. `vectorEffect="non-scaling-stroke"`
 * keeps a crisp hairline at any panel size.
 *
 * Color is token-driven (no raw hex): the accent line uses `--ac` (theme-engine
 * /Steel) with an `hsl(var(--accent))` fallback; the resting hairline uses
 * `--cardBd` with `hsl(var(--border))`. A consumer can override either with the
 * `stroke` prop (e.g. GlassSurface passes `--glassBd` / `--acLine`).
 *
 * SSR / jsdom safe: `useMeasure` returns 0/0 until mount (no `ResizeObserver`),
 * so until measured the overlay renders an empty `<svg>` with no `<path>`.
 * `aria-hidden` — it is pure decoration.
 *
 * @example
 *   <button className={cn(nx.b, "relative", raise)}>
 *     Secondary
 *     <NxKeyline nx="b" />
 *   </button>
 */
export interface NxKeylineProps extends React.SVGAttributes<SVGSVGElement> {
  /** Notch size — MUST match the parent's `nx-*` clip so the stroke registers. Default `"l"`. */
  nx?: NxSize;
  /** Active (focused / selected) — accent stroke at 1.5px instead of the 1px hairline. Default `false`. */
  active?: boolean;
  /**
   * Stroke width in px. Defaults to 1 (resting) / 1.5 (active). The path is
   * inset by half this so the centered stroke stays inside the box.
   */
  strokeWidth?: number;
  /**
   * Stroke color. Defaults to the accent token chain when `active`, the card
   * border chain otherwise. Pass a CSS color / `var(--…)` to override (e.g. the
   * glass rim tokens).
   */
  stroke?: string;
}

const ACTIVE_STROKE = "var(--ac, hsl(var(--accent)))";
const REST_STROKE = "var(--cardBd, hsl(var(--border)))";

export const NxKeyline = React.forwardRef<SVGSVGElement, NxKeylineProps>(
  ({ nx = "l", active = false, strokeWidth, stroke, className, ...rest }, ref) => {
    const [measureRef, { width, height }] = useMeasure<SVGSVGElement>();

    // Merge the internal measure ref with a forwarded ref (rare, but keep it a
    // well-behaved primitive).
    const setRef = React.useCallback(
      (node: SVGSVGElement | null) => {
        measureRef(node);
        if (typeof ref === "function") ref(node);
        else if (ref) (ref as React.MutableRefObject<SVGSVGElement | null>).current = node;
      },
      [measureRef, ref],
    );

    const sw = strokeWidth ?? (active ? 1.5 : 1);
    const strokeColor = stroke ?? (active ? ACTIVE_STROKE : REST_STROKE);
    // Only draw once measured (avoids a degenerate 0x0 path; SSR/jsdom render
    // the empty svg shell). The stroke is centered, so inset by half its width.
    const d = width > 0 && height > 0 ? nxSvgPath(nx, width, height, sw / 2) : null;

    return (
      <svg
        ref={setRef}
        aria-hidden
        className={cn("pointer-events-none absolute inset-0 z-[2]", className)}
        width="100%"
        height="100%"
        viewBox={d ? `0 0 ${width} ${height}` : undefined}
        preserveAspectRatio="none"
        fill="none"
        {...rest}
      >
        {d ? (
          <path
            d={d}
            fill="none"
            stroke={strokeColor}
            strokeWidth={sw}
            vectorEffect="non-scaling-stroke"
          />
        ) : null}
      </svg>
    );
  },
);

NxKeyline.displayName = "NxKeyline";
